import React, { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getProducts } from "../../../common/services/products/productService";
import SearchBox from "../../../common/components/search/searchBox";
import ProductsTable from "./productsTable";
import { useTranslation } from "react-i18next";

const Products = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["products"]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch products on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle product updates
  const handleProductUpdated = useCallback((product) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const index = updatedProducts.findIndex(
        (p) => p.productId === product.productId
      );
      if (index > -1) {
        updatedProducts.splice(index, 1);
      }
      updatedProducts.push(product);
      return updatedProducts;
    });
  }, []);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Handle sorting
  const handleSort = (colId, sort) => {
    if (colId && sort) {
      setSortColumn({
        path: colId,
        order: sort,
      });
    }
  };

  // Handle active toggle
  const handleToggleActiveOnly = () => {
    setActiveOnly(!activeOnly);
  };

  // Handle product selection
  const handleProductSelected = (product, isNew = false) => {
    // Inject the callback function before calling back to the onClick event
    onClick(product, handleProductUpdated, isNew);
  };

  // Get filtered and sorted data
  const getFilteredData = () => {
    let filtered = products;

    if (activeOnly) filtered = filtered.filter((p) => p.active);

    if (searchQuery)
      filtered = filtered.filter(
        (p) =>
          (p.name != null &&
            p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (p.shortName != null &&
            p.shortName.toLowerCase().includes(searchQuery.toLowerCase()))
      );

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    return { totalCount: filtered.length, data: sorted };
  };

  if (isLoading) return <Loading />;

  const { totalCount, data: filteredProducts } = getFilteredData();

  const newProduct = {
    productId: uuid(),
    name: t("NewProduct"),
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() => handleProductSelected(newProduct, true)}
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddProduct")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedProduct", { count: totalCount })}</p>
      <ProductsTable
        products={filteredProducts}
        sortColumn={sortColumn}
        onSort={handleSort}
        onRowClick={handleProductSelected}
      />
    </div>
  );
};

export default Products;
